import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Trophy, Flame, Calendar, Star, Shield } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { Movement } from "./Rankings";
import { getTeam } from "../lib/api";

const RankChart = ({ history }) => {
  if (!history || history.length === 0) return null;
  const W = 640;
  const H = 180;
  const pad = 28;
  const ranks = history.map((h) => h.rank);
  const maxR = Math.max(...ranks);
  const minR = Math.min(...ranks);
  const span = Math.max(1, maxR - minR);
  const stepX = (W - pad * 2) / Math.max(1, history.length - 1);
  // lower rank number = higher on chart
  const y = (r) => pad + ((r - minR) / span) * (H - pad * 2);
  const pts = history.map((h, i) => [pad + i * stepX, y(h.rank)]);
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <path
        d={`${path} L${pts[pts.length - 1][0]},${H - pad} L${pts[0][0]},${H - pad} Z`}
        fill="rgba(127,139,78,0.12)"
      />
      <path d={path} fill="none" stroke="var(--olive-2)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]} cy={p[1]} r="4.5" fill="var(--ink)" />
          <text x={p[0]} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--muted)" fontWeight="600">
            {history[i].week}
          </text>
          <text x={p[0]} y={p[1] - 12} textAnchor="middle" fontSize="12" fill="var(--ink)" fontWeight="700">
            {history[i].rank}
          </text>
        </g>
      ))}
    </svg>
  );
};

const TeamPage = () => {
  const { slug } = useParams();
  const [team, setTeam] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    getTeam(slug)
      .then((t) => {
        setTeam(t);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, [slug]);

  return (
    <div className="App">
      <Header />
      <main className="max-w-[1240px] mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <Link
          to="/#rankings"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--muted)] hover:text-[var(--ink)] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to rankings
        </Link>

        {status === "loading" && (
          <p className="text-[var(--muted)] py-20">Loading team…</p>
        )}
        {status === "error" && (
          <p className="text-[var(--muted)] py-20">Team not found.</p>
        )}

        {status === "ok" && team && (
          <>
            {/* Header block */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 pb-8 border-b border-[var(--line)]">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-bold tracking-eyebrow uppercase text-[var(--olive-2)] mb-3">
                  {team.sport_label} · National rank
                </span>
                <div className="flex items-center gap-4">
                  <span className="font-serif-display text-6xl sm:text-7xl font-semibold text-[var(--olive-2)] leading-none">
                    {team.rank}
                  </span>
                  <div>
                    <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-[var(--ink)] flex items-center gap-3">
                      {team.name}
                      {team.rank === 1 && <Flame className="w-6 h-6 text-[var(--olive-2)]" />}
                    </h1>
                    <p className="mt-2 flex items-center gap-1.5 text-[15px] text-[var(--muted)]">
                      <MapPin className="w-4 h-4" /> {team.location}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] px-5 py-3 text-center">
                  <div className="font-serif-display text-2xl font-semibold text-[var(--ink)]">{team.record}</div>
                  <div className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--muted)] mt-1">Record</div>
                </div>
                <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] px-5 py-3 text-center">
                  <div className="font-serif-display text-2xl font-semibold text-[var(--olive-2)]" data-testid="team-prev-rank">{team.prev_rank || "NR"}</div>
                  <div className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--muted)] mt-1">Previous rank</div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 mt-12">
              {/* Left: rank history + results */}
              <div className="space-y-12">
                <div>
                  <h2 className="text-xl font-extrabold text-[var(--ink)] mb-1">Rank history</h2>
                  <p className="text-sm text-[var(--muted)] mb-5">Previous poll vs. current poll, as published by the ranking source.</p>
                  <div className="rounded-3xl border border-[var(--line)] bg-[var(--paper)] p-5">
                    <RankChart history={team.rank_history} />
                  </div>
                </div>

                <div>
                  <h2 className="text-xl font-extrabold text-[var(--ink)] mb-5 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[var(--olive-2)]" /> Recent results
                  </h2>
                  <div className="rounded-3xl border border-[var(--line)] bg-[var(--paper)] overflow-hidden">
                    {team.results.length === 0 ? (
                      <p className="px-5 py-6 text-[14px] text-[var(--muted)]">
                        Verified game-by-game results aren't loaded yet. Only
                        confirmed fixtures are shown here.
                      </p>
                    ) : (
                      [...team.results]
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((g, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)] last:border-0"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span
                                className={`grid place-items-center w-7 h-7 rounded-full text-[12px] font-bold ${
                                  g.outcome === "W"
                                    ? "bg-[var(--olive)] text-white"
                                    : g.outcome === "L"
                                    ? "bg-[#b5553f] text-white"
                                    : "bg-[var(--cream-2)] text-[var(--ink)]"
                                }`}
                              >
                                {g.outcome}
                              </span>
                              <div className="min-w-0">
                                <div className="text-[15px] font-bold text-[var(--ink)] truncate">
                                  vs {g.opponent}
                                </div>
                                <div className="text-[12px] text-[var(--muted)]" data-testid={`result-meta-${i}`}>
                                  {g.date}
                                  {g.source && ` · Source: ${g.source}`}
                                </div>
                              </div>
                            </div>
                            <span className="text-[15px] font-bold tabular-nums text-[var(--ink)]">
                              {g.score}
                            </span>
                          </div>
                        ))
                    )}
                  </div>
                </div>
              </div>

              {/* Right: schedule */}
              <div>
                <h2 className="text-xl font-extrabold text-[var(--ink)] mb-5 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[var(--olive-2)]" /> Upcoming schedule
                </h2>
                {team.schedule.length === 0 ? (
                  <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-5 text-[14px] text-[var(--muted)]">
                    Schedule to be announced.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {team.schedule.map((g, i) => (
                      <div
                        key={i}
                        className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-5"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--olive-2)]">
                            {g.home ? "Home" : "Away"}
                          </span>
                          <span className="text-[13px] font-semibold text-[var(--muted)]">{g.date}</span>
                        </div>
                        <div className="mt-2 text-[16px] font-bold text-[var(--ink)]">
                          {g.home ? "vs" : "@"} {g.opponent}
                        </div>
                        <div className="text-[13px] text-[var(--muted)]">{g.opp_location}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-10" data-testid="team-leaders">
                  <h2 className="text-xl font-extrabold text-[var(--ink)] mb-5 flex items-center gap-2">
                    <Star className="w-5 h-5 text-[var(--olive-2)]" /> Team leaders
                  </h2>
                  {team.roster ? (
                    <div className="rounded-3xl border border-[var(--line)] bg-[var(--paper)] p-5 space-y-3">
                      {team.roster.captains?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {team.roster.captains.map((c) => (
                            <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--cream-2)] px-3 py-1.5 text-[13px] font-semibold text-[var(--ink)]">
                              <Shield className="w-3.5 h-3.5 text-[var(--olive-2)]" /> {c}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-[12px] text-[var(--muted)]">Source: {team.roster.source}</p>
                    </div>
                  ) : (
                    <div
                      data-testid="team-leaders-unverified"
                      className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-5 text-[14px] text-[var(--muted)]"
                    >
                      No published roster, captains or stat leaders are available from a
                      verifiable source yet, so none are shown.
                    </div>
                  )}
                </div>

              </div>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default TeamPage;
